<template>
  <div ref="target" class="whitespace-nowrap">
    <button :class="selectClasses" @click="handleClick">
      <p class="ellipsis flex-1 text-start pl-2">{{ selectedName }}</p>
      <Icon
        icon="line-md:chevron-up"
        :color="dropdownOpened ? '#00B14F' : '#5B625F'"
        :width="size === 'md' ? '16px' : '20px'"
        :class="'transition-3' + (dropdownOpened ? '' : ' rotate-180')"
      />
    </button>

    <teleport v-if="dropdownOpened" to="body">
      <div :class="selectBoxClasses" :style="selectBoxStyle">
        <div ref="selectBox" :class="buttonContainerClasses">
          <button
            v-for="(button, key) in buttons"
            :key="key"
            :class="[
              'flex items-center justify-between flex-1 rounded-lg hover:bg-black/10 transition-2',
              size === 'lg' ? 'p-4' : 'px-4 py-2',
              button.disabled
                ? 'text-gray-20 cursor-not-allowed'
                : button.value === selected
                  ? dark
                    ? 'text-primary-40'
                    : 'text-primary-50'
                  : dark
                    ? 'text-white'
                    : 'text-gray-95'
            ]"
            :disabled="button.disabled"
            @click="handleOptionClick(button)"
          >
            <p class="ellipsis">
              {{ button.label }}
            </p>
          </button>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { ref, computed, onMounted, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useSelect } from '@/shared/composables/useSelect'
import { useScrollable } from '@/shared/composables/useScrollable'

const { t } = useI18n()
const { selectBoxStyle, updateSelectBoxStyle, setCustomSelectConfig } = useSelect()

const selectBox = ref<HTMLElement | null>(null)
const { isScrollable, checkScrollable } = useScrollable(selectBox)

type Props = {
  size: 'md' | 'lg'
  buttons: SelectButton[]
  placeholder?: string
  disabled?: boolean
  maxViewItems?: number
  defaultText?: string
  dark?: boolean
  surface?: boolean
}

const props = defineProps<Props>()

const target = ref<HTMLElement | null>(null)
const dropdownOpened = ref(false)
const showPlaceholder = ref(false)
const selected = defineModel<string | number>()
const selectButtonLength = computed(() => props.buttons.length)

const spacingMap: Record<string, number> = { md: 8, lg: 12 }
const itemHeight = { md: 37, lg: 40 }

onMounted(() => {
  setSelectConfig()
  checkScrollable()
})

watch(
  () => props.size,
  () => {
    setSelectConfig()
  },
  { immediate: true }
)

watch(
  () => dropdownOpened.value,
  (val) => {
    checkScrollable()
  }
)

function setSelectConfig() {
  const option: { width?: number; height?: number; spacing?: number } = {}
  if (props.maxViewItems) {
    option.height = props.maxViewItems * itemHeight[props.size]
  } else {
    option.height = 5 * itemHeight[props.size]
  }

  option.spacing = spacingMap[props.size]
  setCustomSelectConfig({ ...option })
}

const selectedName = computed(() => {
  const selectedButton = props.buttons.find((button) => button.value === selected.value)
  showPlaceholder.value = false
  if (selectedButton) return selectedButton.label
  if (props.defaultText) return props.defaultText
  showPlaceholder.value = true
  return selectButtonLength.value === 0
    ? t('placeholder.no_selectable_data')
    : props.placeholder
      ? props.placeholder
      : t('placeholder.select_value')
})

onClickOutside(target, (event: Event) => (dropdownOpened.value = false))

const selectClasses = computed(() => {
  const base = 'flex items-center justify-between w-full border rounded-lg transition-2 z-[-1]'

  const sizeMap: Record<string, string> = {
    lg: 'h-24 text-medium gap-4 p-6',
    md: 'h-20 text-small gap-4 px-6 py-5'
  }

  const statusMap: Record<string, string> = {
    default: props.surface
      ? props.dark
        ? 'bg-gray-80 border-gray-70 hover:bg-gray-80-10'
        : 'bg-gray-5 border-gray-20 hover:bg-gray-5-10'
      : props.dark
        ? 'bg-gray-90 border-gray-70 hover:bg-gray-90-10'
        : 'bg-white border-gray-20 hover:bg-gray-5-10',
    focus: props.surface
      ? props.dark
        ? 'bg-gray-80 border-primary-40 hover:bg-gray-40-10'
        : 'bg-gray-5 border-primary-50 hover:bg-gray-5-10'
      : props.dark
        ? 'bg-gray-90 border-primary-40 hover:bg-gray-90-10'
        : 'bg-white border-primary-50 hover:bg-gray-5-10',
    disabled: props.surface
      ? props.dark
        ? 'bg-gray-80 border-gray-70 cursor-not-allowed'
        : 'bg-gray-5 border-gray-20 cursor-not-allowed'
      : props.dark
        ? 'bg-gray-90 border-gray-70 cursor-not-allowed'
        : 'bg-white border-gray-20 cursor-not-allowed'
  }

  const state =
    props.disabled || selectButtonLength.value === 0
      ? 'disabled'
      : dropdownOpened.value
        ? 'focus'
        : 'default'

  const textColorMap: Record<string, string> = {
    default: props.dark ? 'text-white' : 'text-gray-95',
    placeholder: 'text-gray-50',
    disabled: props.dark ? 'text-gray-60' : 'text-gray-20'
  }
  const textColor =
    props.disabled || selectButtonLength.value === 0
      ? textColorMap.disabled
      : showPlaceholder.value
        ? textColorMap.placeholder
        : textColorMap.default

  return [base, sizeMap[props.size], statusMap[state], textColor].join(' ')
})

const selectBoxClasses = computed(() => {
  const base = 'fade-up flex flex-col p-2 rounded-md shadow-level1 transition-2'
  const paddingRight = isScrollable.value ? 'pr-0' : ''

  const darkMode = props.dark ? 'bg-gray-90' : 'bg-white'

  return [base, paddingRight, darkMode].join(' ')
})

const buttonContainerClasses = computed(() => {
  const base = 'h-full flex flex-col overflow-y-auto select-box'

  const sizeMap: Record<string, string> = {
    md: 'gap-4 text-small',
    lg: 'gap-4 text-medium'
  }

  return [base, sizeMap[props.size]].join(' ')
})

const handleClick = (event: MouseEvent) => {
  if (props.disabled || selectButtonLength.value === 0) return
  dropdownOpened.value = !dropdownOpened.value
  if (dropdownOpened.value) {
    updateSelectBoxStyle(target.value)
  }
}

const handleOptionClick = (value: SelectButton) => {
  if (value.disabled) return

  dropdownOpened.value = false
  selected.value = value.value
}
</script>
